import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getFormValues, reduxForm, SubmissionError } from 'redux-form';

import {
  CANCEL_UPLOAD_DIALOG,
  CancelUploadDialog,
  getUploadProgressSelector,
  getUploadVideoObjectSelector,
  isFormFieldSelectbox,
  publishVideoRequestAction,
  UploadFailedDialog,
  uploadFailedSelector,
  uploadFormConfigSelector,
  UploadFormControls,
  UploadProgress,
} from 'pages/upload.page';
import { CustomFieldsMap, FIELDS, videoFormTransform, videoFormValidate } from 'components/forms';
import { ContentBox } from 'components/content-box';
import { BlockUi } from 'components/block-ui';
import { LoadingSpinner } from 'components/loading-spinner';
import { showDialogAction } from 'components/redux-dialog/actions';
import { initialValuesSelector } from 'pages/upload.page/selectors';
import { uploadFormConfigRequestAction } from 'pages/upload.page/actions';

const UPLOAD_VIDEO_FORM = 'uploadVideoForm';

const PublishVideo = (props) => {
  const [showBlockUi, setShowBlockUi] = useState(true);
  const additionalFormData = {};
  const {
    uploadProgress,
    uploadFormConfig,
    fetchUploadFormConfig,
    publishVideo,
    uploadVideoObject,
    handleSubmit,
    formValues,
    showDialog,
    isPodcast,
  } = props;
  const { t } = useTranslation();

  const submitForm = (formData) => {
    return new Promise((resolve, reject) => {
      const data = { ...formData, ...additionalFormData };
      const errors = videoFormValidate(uploadFormConfig, data);

      if (Object.keys(errors).length > 0) {
        reject(new SubmissionError(errors));
      } else {
        publishVideo(videoFormTransform(uploadFormConfig, data, uploadVideoObject.video.id));
        setShowBlockUi(true);
        resolve();
      }
    });
  };

  useEffect(() => {
    if (!uploadFormConfig) {
      fetchUploadFormConfig();
    } else {
      setShowBlockUi(false);
    }

    return () => setShowBlockUi(false);
  }, [uploadFormConfig, fetchUploadFormConfig]);

  return uploadFormConfig ? (
    <div className="ct-publish-video">
      <ContentBox>
        <h2 className="title">{t(isPodcast ? 'podcast_upload' : 'video_upload')}</h2>
        <UploadProgress uploadProgress={uploadProgress} />

        <div className="row no-gutters">
          <form className="form" onSubmit={handleSubmit(submitForm)}>
            {/*Render Form Fields from configuration */}
            {uploadFormConfig.map((cfg, i) => {
              if (cfg.type === FIELDS.TEXT_BOX) {
                return null;
              }

              if (isFormFieldSelectbox(cfg.type)) {
                return CustomFieldsMap[cfg.type](cfg.config, i, cfg.type, UPLOAD_VIDEO_FORM, formValues);
              }

              return CustomFieldsMap[cfg.type](cfg.config, i);
            })}

            <UploadFormControls
              onCancel={() => showDialog()}
              isPublishDisabled={
                Object.keys(videoFormValidate(uploadFormConfig, formValues)).length > 0 || uploadProgress !== 100
              }
            />
          </form>
        </div>

        <UploadFailedDialog video={uploadVideoObject.video} />
        <CancelUploadDialog video={uploadVideoObject.video} isPodcast={isPodcast} />

        <BlockUi show={showBlockUi}>
          <LoadingSpinner />
        </BlockUi>
      </ContentBox>
    </div>
  ) : null;
};

export const PublishVideoDecorated = compose(
  withRouter,
  connect(
    (state) => ({
      uploadProgress: getUploadProgressSelector(state),
      uploadVideoObject: getUploadVideoObjectSelector(state),
      uploadFormConfig: uploadFormConfigSelector(state),
      uploadFailed: uploadFailedSelector(state),
      formValues: getFormValues(UPLOAD_VIDEO_FORM)(state),
      initialValues: initialValuesSelector(state),
    }),
    (dispatch) => ({
      fetchUploadFormConfig: () => dispatch(uploadFormConfigRequestAction()),
      publishVideo: (video) => dispatch(publishVideoRequestAction(video)),
      showDialog: () => dispatch(showDialogAction(CANCEL_UPLOAD_DIALOG)),
    })
  ),
  reduxForm({
    form: UPLOAD_VIDEO_FORM,
  })
)(PublishVideo);

export * from './fragments';
